/**
 * Chat Finalize Queue
 *
 * Serializes post-stream persistence per chat so the composer can unlock
 * while finalize continues, without letting the next send race that write.
 */

const finalizeTails = new Map<string, Promise<void>>()

/**
 * Mark that this chat has an in-flight finalize. Call the returned release
 * in a finally after touchChatActivity + handleCreateMessages complete.
 */
export function beginChatFinalize(chatId: string): () => void {
  const normalizedChatId = chatId.trim()
  if (!normalizedChatId) {
    return () => {}
  }

  let release!: () => void
  const gate = new Promise<void>(resolve => {
    release = resolve
  })

  const previous = finalizeTails.get(normalizedChatId) ?? Promise.resolve()
  const chained = previous.catch(() => undefined).then(() => gate)
  finalizeTails.set(normalizedChatId, chained)

  let released = false
  return () => {
    if (released) return
    released = true
    release()
    void chained.finally(() => {
      if (finalizeTails.get(normalizedChatId) === chained) {
        finalizeTails.delete(normalizedChatId)
      }
    })
  }
}

/** Await any in-flight finalize for this chat before starting a new send. */
export async function waitForChatFinalize(chatId: string | null | undefined) {
  const normalizedChatId = typeof chatId === 'string' ? chatId.trim() : ''
  if (!normalizedChatId) return

  const pending = finalizeTails.get(normalizedChatId)
  if (!pending) return

  await pending.catch(() => undefined)
}
